/**
 * Organization bounded context (TO-411) — tenant-defined unit types and the
 * organizational units arranged in a tree. Unit *types* are tenant runtime
 * configuration (rows), never an application enum; only the unit lifecycle
 * status is a platform enum.
 */

/** Lifecycle status of an organizational unit. Units are never deleted. */
export enum OrganizationUnitStatus {
	ACTIVE = 'ACTIVE',
	INACTIVE = 'INACTIVE',
	CLOSED = 'CLOSED',
	PLANNED = 'PLANNED',
}

/**
 * Stable machine-readable error codes of the organization API. They prefix the
 * error message (`CODE: human text`), the platform's domain-error convention.
 */
export enum OrgErrorCode {
	VALIDATION_FAILED = 'ORG_VALIDATION_FAILED',
	TYPE_CODE_TAKEN = 'ORG_UNIT_TYPE_CODE_TAKEN',
	TYPE_CODE_IMMUTABLE = 'ORG_UNIT_TYPE_CODE_IMMUTABLE',
	TYPE_NOT_FOUND = 'ORG_UNIT_TYPE_NOT_FOUND',
	TYPE_IN_USE = 'ORG_UNIT_TYPE_IN_USE',
	UNIT_CODE_TAKEN = 'ORG_UNIT_CODE_TAKEN',
	IMMUTABLE_FIELD = 'ORG_UNIT_IMMUTABLE_FIELD',
	PARENT_NOT_FOUND = 'ORG_UNIT_PARENT_NOT_FOUND',
	SELF_PARENT = 'ORG_UNIT_SELF_PARENT',
	HIERARCHY_CYCLE = 'ORG_UNIT_HIERARCHY_CYCLE',
	PARENT_TYPE_NOT_ALLOWED = 'ORG_UNIT_PARENT_TYPE_NOT_ALLOWED',
	PARENT_INACTIVE = 'ORG_UNIT_PARENT_INACTIVE',
	VALIDITY_INVALID = 'ORG_UNIT_VALIDITY_INVALID',
	HAS_ACTIVE_DESCENDANTS = 'ORG_UNIT_HAS_ACTIVE_DESCENDANTS',
	// Organization unit file import (TO-401).
	IMPORT_FILE_HAS_CYCLE = 'ORG_UNIT_IMPORT_FILE_HAS_CYCLE',
	IMPORT_STRATEGY_UNSUPPORTED = 'ORG_UNIT_IMPORT_STRATEGY_UNSUPPORTED',
	// A DEACTIVATE names a code with no unit of the import's source system (TO-540).
	IMPORT_UNIT_NOT_FOUND = 'ORG_UNIT_IMPORT_UNIT_NOT_FOUND',
	// Locations (TO-387)
	LOCATION_COORDINATES_INCOMPLETE = 'ORG_LOCATION_COORDINATES_INCOMPLETE',
	LOCATION_PRIMARY_EXISTS = 'ORG_LOCATION_PRIMARY_EXISTS',
	// User ↔ unit assignments (TO-412).
	HAS_CURRENT_ASSIGNMENTS = 'ORG_UNIT_HAS_CURRENT_ASSIGNMENTS',
	ASSIGNMENT_VALIDITY_INVALID = 'ORG_ASSIGNMENT_VALIDITY_INVALID',
	ASSIGNMENT_UNIT_NOT_FOUND = 'ORG_ASSIGNMENT_UNIT_NOT_FOUND',
	ASSIGNMENT_UNIT_INACTIVE = 'ORG_ASSIGNMENT_UNIT_INACTIVE',
	ASSIGNMENT_UNIT_TYPE_NO_USERS = 'ORG_ASSIGNMENT_UNIT_TYPE_NO_USERS',
	ASSIGNMENT_DUPLICATE = 'ORG_ASSIGNMENT_DUPLICATE',
	ASSIGNMENT_PRIMARY_REQUIRED = 'ORG_ASSIGNMENT_PRIMARY_REQUIRED',
	ASSIGNMENT_PRIMARY_NOT_OPEN_ENDED = 'ORG_ASSIGNMENT_PRIMARY_NOT_OPEN_ENDED',
	ASSIGNMENT_PRIMARY_IN_USE = 'ORG_ASSIGNMENT_PRIMARY_IN_USE',
	ASSIGNMENT_ALREADY_ENDED = 'ORG_ASSIGNMENT_ALREADY_ENDED',
}

/** The role a user holds in an organizational unit (TO-412). Temporariness is the validity window, not a type. */
export enum OrganizationAssignmentType {
	MEMBER = 'MEMBER',
	MANAGER = 'MANAGER',
}

/** Derived from `validTo` against now: CURRENT while open-ended or not yet lapsed. */
export enum OrganizationAssignmentState {
	CURRENT = 'CURRENT',
	ENDED = 'ENDED',
}

/** `filters[state]` of the assignment list: a state, or every assignment. */
export enum OrganizationAssignmentStateFilter {
	CURRENT = 'CURRENT',
	ENDED = 'ENDED',
	ALL = 'ALL',
}

export interface IOrganizationType {
	id: string
	tenantId: string
	code: string
	name: string
	isPhysical: boolean
	canHaveUsers: boolean
	sortOrder: number
	metadata: Record<string, unknown>
	/** Type codes allowed as a unit's parent type; `null` = any parent type. */
	allowedParentTypeCodes: string[] | null
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

export interface IOrganizationUnit {
	id: string
	tenantId: string
	code: string
	externalId: string | null
	name: string
	shortName: string | null
	typeId: string
	typeCode: string
	parentId: string | null
	parentCode: string | null
	status: OrganizationUnitStatus
	isPhysical: boolean
	institutionCode: string | null
	countryCode: string | null
	timezone: string | null
	metadata: Record<string, unknown>
	validFrom: Date | null
	validTo: Date | null
	createdAt: Date
	updatedAt: Date
}

/**
 * Role of a unit's physical location (TO-387). A unit has at most one live
 * `PRIMARY` location — the one its contact card shows.
 */
export enum OrganizationLocationType {
	PRIMARY = 'PRIMARY',
	MAILING = 'MAILING',
	OPERATIONAL = 'OPERATIONAL',
}

/** A physical location of an organizational unit (TO-387). Soft-deleted via `deletedAt`. */
export interface IOrganizationLocation {
	id: string
	tenantId: string
	unitId: string
	locationType: OrganizationLocationType
	addressLine1: string
	addressLine2: string | null
	postalCode: string | null
	city: string
	province: string | null
	region: string | null
	countryCode: string
	latitude: number | null
	longitude: number | null
	phone: string | null
	email: string | null
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

/** A user's assignment to an organizational unit (TO-412). Never deleted; ending stamps `validTo`. */
export interface IOrganizationAssignment {
	id: string
	tenantId: string
	/** Opaque tenant-local user reference — not checked against the identity directory. */
	userId: string
	unitId: string
	unitCode: string
	unitName: string
	assignmentType: OrganizationAssignmentType
	isPrimary: boolean
	validFrom: Date
	validTo: Date | null
	createdAt: Date
	createdBy: string | null
	updatedAt: Date
	endedBy: string | null
}
